/**
 * Centralized Error Handler (NFR-002)
 *
 * Provides utilities for error capture, logging, and reporting.
 * Can be extended to integrate with external services like Sentry.
 */

export interface ErrorContext {
  /** Component or function name where error occurred */
  source?: string;
  /** Additional data to help debug the error */
  extra?: Record<string, unknown>;
  /** User ID if available */
  userId?: string;
  /** Request ID for tracing */
  requestId?: string;
  /** Tags for categorization */
  tags?: Record<string, string>;
}

export interface LoggedError {
  message: string;
  stack?: string;
  context: ErrorContext;
  timestamp: string;
  severity: "error" | "warning" | "info";
}

/**
 * Captures and logs an error with context
 */
export function captureError(
  error: Error | unknown,
  context: ErrorContext = {},
  severity: "error" | "warning" | "info" = "error"
): void {
  const errorObj = error instanceof Error ? error : new Error(String(error));

  const loggedError: LoggedError = {
    message: errorObj.message,
    stack: errorObj.stack,
    context,
    timestamp: new Date().toISOString(),
    severity,
  };

  // Log to console with structured format
  const logMethod = severity === "error" ? console.error : severity === "warning" ? console.warn : console.log;

  logMethod(`[${severity.toUpperCase()}]`, {
    message: loggedError.message,
    source: context.source,
    timestamp: loggedError.timestamp,
    ...(context.extra && { extra: context.extra }),
    ...(context.tags && { tags: context.tags }),
  });

  // In development, also log the stack trace
  if (process.env.NODE_ENV === "development" && errorObj.stack) {
    console.error(errorObj.stack);
  }

  // Future: Send to external error tracking service
  // if (typeof window !== 'undefined') {
  //   Sentry.captureException(errorObj, { extra: context });
  // }
}

/**
 * Creates a wrapped function that captures errors automatically
 */
export function withErrorCapture<T extends (...args: unknown[]) => unknown>(
  fn: T,
  context: ErrorContext
): T {
  return ((...args: Parameters<T>) => {
    try {
      const result = fn(...args);

      // Handle async functions
      if (result instanceof Promise) {
        return result.catch((error) => {
          captureError(error, context);
          throw error;
        });
      }

      return result;
    } catch (error) {
      captureError(error, context);
      throw error;
    }
  }) as T;
}

/**
 * Server action error wrapper
 * Catches errors and returns a consistent error response
 */
export async function safeServerAction<T>(
  action: () => Promise<T>,
  context: ErrorContext
): Promise<{ success: true; data: T } | { success: false; error: string }> {
  try {
    const data = await action();
    return { success: true, data };
  } catch (error) {
    captureError(error, context);

    return {
      success: false,
      error: error instanceof Error ? error.message : "An unexpected error occurred",
    };
  }
}

/**
 * API route error handler
 * Returns appropriate HTTP status codes based on error type
 */
export function getErrorResponse(error: unknown): {
  message: string;
  status: number;
} {
  if (error instanceof Error) {
    // Check for common error types
    if (error.message.includes("not found") || error.message.includes("Not found")) {
      return { message: error.message, status: 404 };
    }
    if (error.message.includes("unauthorized") || error.message.includes("Unauthorized")) {
      return { message: error.message, status: 401 };
    }
    if (error.message.includes("forbidden") || error.message.includes("Forbidden")) {
      return { message: error.message, status: 403 };
    }
    if (error.message.includes("validation") || error.message.includes("invalid")) {
      return { message: error.message, status: 400 };
    }

    return { message: error.message, status: 500 };
  }

  return { message: "An unexpected error occurred", status: 500 };
}
